/** JT1078 live-voice binary frame: magic + codec + seq + time + 160 G.711 bytes. */
export const VOICE_MAGIC = 0xd7;
export const VOICE_HEADER = 8;
export const G711_FRAME_BYTES = 160;

export type G711Codec = "pcma" | "pcmu";

export type VoicePacket = {
  codec: G711Codec;
  seq: number;
  t: number;
  payload: Uint8Array;
};

export function codecFromByte(b: number): G711Codec {
  return b === 0 || b === 7 ? "pcmu" : "pcma";
}

/** Packed send time is Date.now() truncated to uint32. Age is unsigned wrap. */
export function voiceAgeMs(packedT: number, now = Date.now()): number {
  if (!packedT) return 0;
  const age = ((now >>> 0) - (packedT >>> 0)) >>> 0;
  return age > 30_000 ? 0 : age;
}

export function parseVoicePacket(raw: Uint8Array): VoicePacket | null {
  if (raw.length >= VOICE_HEADER + 1 && raw[0] === VOICE_MAGIC) {
    const body = raw.subarray(VOICE_HEADER);
    if (!body.length) return null;
    return {
      codec: codecFromByte(raw[1] ?? 6),
      seq: ((raw[2] ?? 0) << 8) | (raw[3] ?? 0),
      t: (((raw[4] ?? 0) << 24) | ((raw[5] ?? 0) << 16) | ((raw[6] ?? 0) << 8) | (raw[7] ?? 0)) >>> 0,
      payload: body,
    };
  }
  if (raw.length > 0 && raw[0] !== 0x7b) {
    return { codec: "pcma", seq: -1, t: 0, payload: raw };
  }
  return null;
}

/** Emit only complete 160-byte G.711 frames. Leftover stays until the next packet. */
export class G711Assembler {
  private leftover = new Uint8Array(0);

  push(chunk: Uint8Array): Uint8Array[] {
    if (!chunk.length) return [];
    const merged = new Uint8Array(this.leftover.length + chunk.length);
    merged.set(this.leftover);
    merged.set(chunk, this.leftover.length);
    const frames: Uint8Array[] = [];
    let offset = 0;
    while (offset + G711_FRAME_BYTES <= merged.length) {
      frames.push(merged.slice(offset, offset + G711_FRAME_BYTES));
      offset += G711_FRAME_BYTES;
    }
    this.leftover = merged.slice(offset);
    return frames;
  }

  reset(): void {
    this.leftover = new Uint8Array(0);
  }
}
